import React, { useState, useRef } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { X, Upload, Image, Link as LinkIcon, Check } from 'lucide-react';

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageSelected: (imageUrl: string) => void;
  currentUrl?: string;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  onImageSelected,
  currentUrl = ''
}) => {
  const [selectedTab, setSelectedTab] = useState<'upload' | 'url'>('upload');
  const [imageUrl, setImageUrl] = useState(currentUrl);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentUrl || null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Create object URL for preview
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    
    // In a real app, you would upload the file to your server here
    // and get back a URL to the uploaded file
    // For now, we'll just use the object URL for demonstration
  };
  
  // Handle drag events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    
    // Create object URL for preview
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    
    // In a real app, you would upload the file to your server here
  };
  
  // Handle URL input change
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageUrl(e.target.value);
    
    // Update preview only if the URL looks valid
    if (e.target.value.match(/^https?:\/\/.+\.(jpeg|jpg|png|gif|svg)(\?.*)?$/i)) {
      setPreviewUrl(e.target.value);
    } else if (previewUrl && previewUrl !== currentUrl) {
      setPreviewUrl(null);
    }
  };
  
  // Handle confirmation
  const handleConfirm = () => {
    if (selectedTab === 'upload' && previewUrl) {
      onImageSelected(previewUrl);
    } else if (selectedTab === 'url' && imageUrl) {
      onImageSelected(imageUrl);
    }
    onClose();
  };
  
  // Handle tab change
  const handleTabChange = (tab: 'upload' | 'url') => {
    setSelectedTab(tab);
    if (tab === 'url') {
      setPreviewUrl(imageUrl && imageUrl.match(/^https?:\/\/.+\.(jpeg|jpg|png|gif|svg)(\?.*)?$/i) ? imageUrl : null);
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Product Logo"
      size="md"
    >
      <div className="mb-4">
        <div className="flex border-b border-gray-700">
          <button
            className={`px-4 py-2 font-medium text-sm ${selectedTab === 'upload' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-gray-300'}`}
            onClick={() => handleTabChange('upload')}
          >
            Upload Image
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${selectedTab === 'url' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-gray-300'}`}
            onClick={() => handleTabChange('url')}
          >
            Image URL
          </button>
        </div>
      </div>
      
      {selectedTab === 'upload' && (
        <div className="mb-4">
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 hover:border-gray-600'}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/jpeg,image/png,image/gif,image/svg+xml"
              onChange={handleFileChange}
            />
            
            {previewUrl ? (
              <div className="relative">
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="max-h-48 mx-auto object-contain rounded"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewUrl(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="absolute top-2 right-2 bg-gray-800 p-1 rounded-full hover:bg-gray-700"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <Upload size={36} className="mx-auto mb-3 text-gray-500" />
                <p className="mb-1 font-medium">Drag and drop or click to upload</p>
                <p className="text-sm text-gray-400">Supports JPG, PNG, GIF, SVG (Max 2MB)</p>
              </>
            )}
          </div>
        </div>
      )}
      
      {selectedTab === 'url' && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Image URL</label>
          <div className="flex items-center gap-2">
            <div className="flex-grow relative">
              <LinkIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={imageUrl}
                onChange={handleUrlChange}
                placeholder="https://example.com/logo.png"
                className="form-input pl-10 w-full bg-gray-800/70 border border-gray-700"
              />
            </div>
          </div>
          
          {previewUrl && (
            <div className="mt-4 relative">
              <img 
                src={previewUrl} 
                alt="Preview" 
                className="max-h-48 mx-auto object-contain rounded"
                onError={() => setPreviewUrl(null)}
              />
            </div>
          )}
        </div>
      )}
      
      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-700">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleConfirm}
          disabled={!previewUrl}
        >
          <Check size={16} className="mr-1.5" />
          Select Image
        </Button>
      </div>
    </Modal>
  );
};

export default ImageUploadModal; 